import { useState } from 'react';
import { Link } from 'react-router';
import { CATEGORY_META, NOTIFICATION_CATEGORIES, type NotificationCategory, type NotificationDTO } from '@devrobot/shared';
import { Bell, BellOff, Check, Settings2, X } from 'lucide-react';
import { useDismissNotification, useMarkAllRead, useMarkRead, useNotifications } from '../../api/notifications';
import { EmptyState } from '../../components/EmptyState';
import { ErrorAlert } from '../../components/ErrorAlert';
import { PageHeader } from '../../components/PageHeader';
import { RelativeTime } from '../../components/RelativeTime';
import { SkeletonLines } from '../../components/Skeleton';
import { SEVERITY } from '../../components/notifications/severity';

export default function NotificationsPage() {
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [category, setCategory] = useState<NotificationCategory | ''>('');
  const filters = { unread: unreadOnly || undefined, category: category || undefined };
  const query = useNotifications(filters);
  const markAllRead = useMarkAllRead();

  const pages = query.data?.pages ?? [];
  const items = pages.flatMap((p) => p.items);
  const unread = pages[0]?.unread ?? 0;

  return (
    <div className="animate-rise">
      <PageHeader
        title="Notifications"
        subtitle="Deploys, failures and alerts for the projects you can see."
        badges={unread > 0 ? <span className="badge badge-error badge-soft badge-sm">{unread} unread</span> : undefined}
        actions={
          <div className="flex items-center gap-2">
            <button type="button" className="btn btn-sm" disabled={unread === 0 || markAllRead.isPending} onClick={() => markAllRead.mutate({ category: category || undefined })}>
              <Check className="size-4" aria-hidden="true" />
              Mark all read
            </button>
            <Link to="/settings?tab=notifications" className="btn btn-sm btn-ghost">
              <Settings2 className="size-4" aria-hidden="true" />
              <span className="max-sm:hidden">Settings</span>
            </Link>
          </div>
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <label className="flex cursor-pointer items-center gap-2 text-sm">
          <input type="checkbox" className="toggle toggle-sm toggle-primary" checked={unreadOnly} onChange={(e) => setUnreadOnly(e.target.checked)} />
          Unread only
        </label>
        <select className="select select-sm w-auto" value={category} onChange={(e) => setCategory(e.target.value as NotificationCategory | '')} aria-label="Filter by category">
          <option value="">All categories</option>
          {NOTIFICATION_CATEGORIES.map((c) => (
            <option key={c} value={c}>
              {CATEGORY_META[c].label}
            </option>
          ))}
        </select>
      </div>

      {query.error ? <ErrorAlert error={query.error} /> : null}

      {query.isPending ? (
        <SkeletonLines lines={6} />
      ) : items.length === 0 ? (
        <EmptyState
          icon={unreadOnly ? BellOff : Bell}
          title={unreadOnly ? 'Nothing unread' : 'No notifications yet'}
        >
          {unreadOnly ? 'You are all caught up.' : 'Deploys, failures and alerts for your projects will appear here.'}
        </EmptyState>
      ) : (
        <>
          <ul className="list card dr-surface">
            {items.map((n) => (
              <Row key={n.id} n={n} />
            ))}
          </ul>
          {query.hasNextPage && (
            <div className="mt-4 flex justify-center">
              <button type="button" className="btn btn-sm" onClick={() => void query.fetchNextPage()} disabled={query.isFetchingNextPage}>
                {query.isFetchingNextPage ? <span className="loading loading-spinner loading-xs" /> : null}
                Load more
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function Row({ n }: { n: NotificationDTO }) {
  const markRead = useMarkRead();
  const dismiss = useDismissNotification();
  const s = SEVERITY[n.severity];
  const Icon = s.icon;

  return (
    <li className={`list-row items-start gap-3 ${n.readAt ? 'opacity-60' : ''}`}>
      <span className={`${s.tint} grid size-9 shrink-0 place-items-center rounded-full`} title={s.label}>
        <Icon className="size-4" />
      </span>

      <div className="list-col-grow min-w-0">
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
          {n.href ? (
            <Link to={n.href} className="font-medium hover:underline" onClick={() => !n.readAt && markRead.mutate([n.id])}>
              {n.title}
            </Link>
          ) : (
            <span className="font-medium">{n.title}</span>
          )}
          <span className={`${s.badge} badge badge-soft badge-xs`}>{s.label}</span>
          {!n.readAt && <span className="badge badge-primary badge-xs">New</span>}
        </div>
        {n.body && <p className="mt-0.5 text-sm text-base-content/70">{n.body}</p>}
        <p className="mt-1 flex flex-wrap items-center gap-x-2 text-xs text-base-content/55">
          <RelativeTime value={n.createdAt} />
          <span>· {CATEGORY_META[n.category].label}</span>
          {n.projectKey && (
            <span className="truncate">
              · {n.projectKey}
              {n.environment ? `/${n.environment}` : ''}
            </span>
          )}
        </p>
      </div>

      <div className="flex shrink-0 items-center gap-1">
        {!n.readAt && (
          <button type="button" className="btn btn-square btn-ghost btn-xs" aria-label="Mark as read" title="Mark as read" onClick={() => markRead.mutate([n.id])}>
            <Check className="size-3.5" aria-hidden="true" />
          </button>
        )}
        <button type="button" className="btn btn-square btn-ghost btn-xs" aria-label="Dismiss" title="Dismiss" onClick={() => dismiss.mutate(n.id)}>
          <X className="size-3.5" aria-hidden="true" />
        </button>
      </div>
    </li>
  );
}
